import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Loader2, Save, MousePointerClick, ExternalLink, Share2 } from "lucide-react";

export const GallerySettingsManager = () => {
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({ total: 0, last7: 0, last24h: 0, bySource: {} as Record<string, number> });

  const load = async () => {
    setLoading(true);
    const [{ data: settings }, { data: events }] = await Promise.all([
      (supabase.from("app_settings" as any) as any).select("value").eq("key", "gallery_link").maybeSingle(),
      (supabase.from("gallery_click_events" as any) as any).select("source,created_at").order("created_at", { ascending: false }).limit(2000),
    ]);
    setUrl(settings?.value?.url || "");
    setLabel(settings?.value?.label || "");
    const now = Date.now();
    const list = (events as any[]) || [];
    const last7 = list.filter((e) => now - new Date(e.created_at).getTime() < 7 * 86400000).length;
    const last24h = list.filter((e) => now - new Date(e.created_at).getTime() < 86400000).length;
    const bySource: Record<string, number> = {};
    list.forEach((e) => { const s = e.source || "unknown"; bySource[s] = (bySource[s] || 0) + 1; });
    setStats({ total: list.length, last7, last24h, bySource });
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    const { error } = await (supabase.from("app_settings" as any) as any)
      .upsert({ key: "gallery_link", value: { url, label }, updated_at: new Date().toISOString() }, { onConflict: "key" });
    setSaving(false);
    if (error) toast({ title: "Save failed", description: error.message, variant: "destructive" });
    else toast({ title: "Saved", description: "Gallery link updated." });
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ExternalLink className="w-5 h-5" /> Gallery Link Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Button label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="OFUESI-2024 Pictures" />
          </div>
          <div>
            <Label>Gallery URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." />
          </div>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><MousePointerClick className="w-4 h-4" /> Total clicks</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold">{stats.total}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Last 7 days</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold">{stats.last7}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Last 24 hours</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold">{stats.last24h}</div></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Share2 className="w-5 h-5" /> Clicks by source</CardTitle></CardHeader>
        <CardContent>
          {Object.keys(stats.bySource).length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No clicks recorded yet.</p>
          ) : (
            <ul className="divide-y">
              {Object.entries(stats.bySource).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <li key={k} className="flex justify-between py-2 text-sm">
                  <span className="capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="font-semibold">{v}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
